import { useState, useEffect } from "react";
import api, { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Sparkles, Loader2, Wand2, X, Check, Trash2, AlertTriangle, Layers, Package } from "lucide-react";
import { toast } from "sonner";

/**
 * AiStockDraftDialog — AI ile Kumaş/Aksesuar oluşturucu (Turn N+1)
 * kind: "fabrics" | "accessories"
 * Türkçe komut → N renk = N ayrı taslak → Preview + HEPSİNE UYGULA + duplicate → Commit
 */
export default function AiStockDraftDialog({ open, onClose, onCreated, kind }) {
  const isFabric = kind === "fabrics";
  const label = isFabric ? "Kumaş" : "Aksesuar";
  const emoji = isFabric ? <Layers className="w-4 h-4" /> : <Package className="w-4 h-4" />;

  const [prompt, setPrompt] = useState("");
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [bulk, setBulk] = useState({});

  useEffect(() => {
    if (!open) { setPrompt(""); setDraft(null); setBulk({}); }
  }, [open]);

  const generate = async () => {
    if (!prompt.trim()) return toast.error("Komut yazın");
    setBusy(true);
    try {
      const { data } = await api.post("/copilot/stock-draft", { kind, prompt: prompt.trim() });
      setDraft(data);
      toast.success(`${data.cards.length} taslak hazırlandı`);
    } catch (e) { toast.error(formatApiError(e) || "AI taslak üretemedi"); }
    finally { setBusy(false); }
  };

  const patchDraft = async (updates) => {
    if (!draft) return;
    try {
      const { data } = await api.patch(`/copilot/stock-draft/${draft.id}`, updates);
      setDraft(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const updateCard = (idx, patch) => {
    const next = draft.cards.map((c, i) => i === idx ? { ...c, ...patch } : c);
    patchDraft({ cards: next });
  };
  const removeCard = (idx) => patchDraft({ cards: draft.cards.filter((_, i) => i !== idx) });
  const applyBulk = () => {
    const clean = Object.fromEntries(Object.entries(bulk).filter(([, v]) => v !== "" && v !== null));
    if (Object.keys(clean).length === 0) return toast.error("Uygulanacak alan yok");
    patchDraft({ apply_to_all: clean });
    setBulk({});
    toast.success("Tüm satırlara uygulandı");
  };

  const commit = async (force = false) => {
    setSaving(true);
    try {
      const { data } = await api.post(`/copilot/stock-draft/${draft.id}/commit${force ? "?force=true" : ""}`);
      if (data.conflict) {
        if (window.confirm(`${data.duplicates.length} kart zaten mevcut:\n${data.duplicates.map(d => "- " + d.name + " (" + d.existing_code + ")").join("\n")}\n\nDuplicate satırları atlayarak devam edilsin mi?`)) {
          return commit(true);
        }
        return;
      }
      toast.success(data.message);
      window.dispatchEvent(new CustomEvent(isFabric ? "fucan:fabrics-refresh" : "fucan:accessories-refresh"));
      onCreated?.(data);
      onClose?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const bulkFields = isFabric
    ? [["fabric_type", "Kumaş Türü"], ["composition", "Kompozisyon"], ["weight_gsm", "Gramaj (g/m²)", "number"], ["min_stock_kg", "Kritik Stok (kg)", "number"], ["location", "Konum"]]
    : [["category", "Kategori"], ["unit", "Birim"], ["size", "Ölçü"], ["min_stock", "Kritik Stok", "number"], ["location", "Konum"]];

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent
        data-testid="ai-stock-dialog"
        className="w-[95vw] max-w-[95vw] sm:max-w-4xl lg:max-w-5xl max-h-[92vh] flex flex-col p-0 gap-0 bg-slate-950 border-slate-800"
      >
        <DialogHeader className="px-5 py-4 border-b border-slate-800 bg-gradient-to-br from-slate-900 to-slate-950">
          <DialogTitle className="flex items-center gap-2 text-slate-100">
            <div className="w-8 h-8 rounded-sm bg-gradient-to-br from-blue-600 to-blue-700 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <div>
              <div className="text-sm font-bold">AI ile {label} Oluştur</div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">
                Her Renk = Ayrı Stok Kartı · Preview Zorunlu · DB'ye Yazılmaz
              </div>
            </div>
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
          {!draft ? (
            <>
              <div className="rounded-sm border border-blue-900/40 bg-blue-950/20 p-3 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-blue-400 shrink-0 mt-0.5" />
                <div className="text-[11px] text-blue-200">
                  {label} adı ve renkleri yazın. AI her renk için AYRI stok kartı taslağı üretir.
                  Bilinmeyen fiyat/stok/tedarikçi UYDURULMAZ.
                </div>
              </div>
              <Textarea
                data-testid="ai-stock-prompt"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) generate(); }}
                rows={4}
                placeholder={isFabric
                  ? "Örn: Süprem penye; lacivert, siyah, beyaz, sarı, hardal sarı. Gramaj 220 gr/m² kompozisyon %100 pamuk."
                  : "Örn: Gömlek düğmesi; beyaz, siyah, lacivert; 18L; adet."}
                className="bg-slate-900 border-slate-800 text-slate-100 text-sm rounded-sm"
              />
            </>
          ) : (
            <>
              <div className="text-[11px] text-slate-400 flex items-center gap-2">
                {emoji} <b className="text-slate-200">{draft.base_name}</b> · {draft.cards.length} taslak kart
              </div>

              {/* HEPSİNE UYGULA */}
              <section className="rounded-sm border border-fuchsia-900/40 bg-fuchsia-950/10 p-3">
                <div className="text-[10px] uppercase tracking-widest text-fuchsia-300 font-mono mb-2">
                  ⚡ Hepsine Uygula (ortak alanlar — renk hariç)
                </div>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                  {bulkFields.map(([k, l, t]) => (
                    <div key={k}>
                      <label className="text-[9px] text-slate-500 uppercase">{l}</label>
                      <Input
                        type={t || "text"}
                        value={bulk[k] ?? ""}
                        onChange={(e) => setBulk({ ...bulk, [k]: t === "number" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value })}
                        className="h-7 text-[11px] bg-slate-900 border-slate-800 rounded-sm"
                        data-testid={`ai-stock-bulk-${k}`}
                      />
                    </div>
                  ))}
                </div>
                <Button size="sm" onClick={applyBulk} className="mt-2 bg-fuchsia-700 hover:bg-fuchsia-600 rounded-sm h-7 text-[11px]" data-testid="ai-stock-bulk-apply">
                  <Check className="w-3 h-3 mr-1.5" /> Hepsine Uygula
                </Button>
              </section>

              {/* Kartlar */}
              <div className="space-y-1.5">
                {draft.cards.map((c, i) => (
                  <div key={c.temp_id || i}
                       data-testid={`ai-stock-card-${i}`}
                       className={`rounded-sm border p-2 ${c.duplicate_of ? "border-red-800 bg-red-950/10" : "border-slate-800 bg-slate-900/40"}`}>
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className="text-xs text-slate-100 font-semibold flex-1">{c.name}</span>
                      {c.duplicate_of && (
                        <Badge className="bg-red-950 text-red-300 border border-red-800 text-[9px]">
                          MEVCUT: {c.duplicate_of.code}
                        </Badge>
                      )}
                      <button onClick={() => removeCard(i)} className="text-slate-500 hover:text-red-400 p-0.5" data-testid={`ai-stock-remove-${i}`}>
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-1.5">
                      <Field label="Renk" v={c.color} on={(v) => updateCard(i, { color: v })} testId={`ai-stock-color-${i}`} />
                      {isFabric ? (
                        <>
                          <Field label="Tür" v={c.fabric_type} on={(v) => updateCard(i, { fabric_type: v })} />
                          <Field label="Kompozisyon" v={c.composition} on={(v) => updateCard(i, { composition: v })} />
                          <Field label="Gramaj" v={c.weight_gsm} on={(v) => updateCard(i, { weight_gsm: v === "" ? null : Number(v) })} type="number" />
                          <Field label="Konum" v={c.location} on={(v) => updateCard(i, { location: v })} />
                        </>
                      ) : (
                        <>
                          <Field label="Kategori" v={c.category} on={(v) => updateCard(i, { category: v })} />
                          <Field label="Birim" v={c.unit} on={(v) => updateCard(i, { unit: v })} />
                          <Field label="Ölçü" v={c.size} on={(v) => updateCard(i, { size: v })} />
                          <Field label="Konum" v={c.location} on={(v) => updateCard(i, { location: v })} />
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        <DialogFooter className="border-t border-slate-800 bg-slate-950/80 px-5 py-3 gap-2">
          {!draft ? (
            <>
              <Button variant="outline" onClick={() => onClose?.()}>Kapat</Button>
              <Button onClick={generate} disabled={busy || !prompt.trim()} className="bg-blue-600 hover:bg-blue-500 rounded-sm" data-testid="ai-stock-generate">
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wand2 className="w-4 h-4 mr-2" />}
                Taslakları Oluştur
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => setDraft(null)} className="rounded-sm border-slate-700"><X className="w-4 h-4 mr-1.5" /> Vazgeç</Button>
              <Button
                onClick={() => commit(false)}
                disabled={saving || draft.cards.length === 0}
                className="bg-emerald-600 hover:bg-emerald-500 rounded-sm uppercase text-xs font-semibold tracking-wider"
                data-testid="ai-stock-commit"
              >
                <Check className="w-4 h-4 mr-2" /> {draft.cards.length} {label} Oluştur
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, v, on, type = "text", testId }) {
  return (
    <div>
      <div className="text-[9px] uppercase text-slate-500">{label}</div>
      <Input type={type} value={v ?? ""} onChange={(e) => on(e.target.value)}
             data-testid={testId}
             className="h-7 text-[11px] bg-slate-900 border-slate-800 rounded-sm" />
    </div>
  );
}
